/**
 * Reusable modal dialog component.
 * Creates a backdrop + dialog box with header, body, and footer.
 * Handles Escape to close, focus trapping, and ARIA.
 */

export interface DialogAction {
  label: string;
  style?: 'primary' | 'success' | 'danger';
  onClick: () => void;
  disabled?: boolean;
}

export interface DialogConfig {
  title: string;
  onClose: () => void;
  actions?: DialogAction[];
}

export class Dialog {
  private backdrop: HTMLDivElement;
  private dialog: HTMLDivElement;
  private bodyEl: HTMLDivElement;
  private previousFocus: HTMLElement | null = null;
  private config: DialogConfig;

  constructor(config: DialogConfig) {
    this.config = config;
    this.previousFocus = document.activeElement as HTMLElement;

    // Backdrop
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'dialog-backdrop';
    this.backdrop.addEventListener('mousedown', (e) => {
      e.stopPropagation();
      if (e.target === this.backdrop) this.close();
    });
    // Block all clicks from reaching elements behind the backdrop
    this.backdrop.addEventListener('click', (e) => e.stopPropagation());
    this.backdrop.addEventListener('touchstart', (e) => e.stopPropagation());

    // Dialog box
    this.dialog = document.createElement('div');
    this.dialog.className = 'dialog';
    this.dialog.setAttribute('role', 'dialog');
    this.dialog.setAttribute('aria-modal', 'true');
    this.dialog.setAttribute('aria-label', config.title);

    // Header
    const header = document.createElement('div');
    header.className = 'dialog__header';
    const titleEl = document.createElement('span');
    titleEl.textContent = config.title;
    header.appendChild(titleEl);
    const closeBtn = document.createElement('button');
    closeBtn.className = 'btn btn--icon';
    closeBtn.textContent = '×';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);
    this.dialog.appendChild(header);

    // Body
    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'dialog__body';
    this.dialog.appendChild(this.bodyEl);

    // Footer with actions
    if (config.actions && config.actions.length > 0) {
      const footer = document.createElement('div');
      footer.className = 'dialog__footer';
      for (const action of config.actions) {
        const btn = document.createElement('button');
        btn.className = `btn${action.style ? ` btn--${action.style}` : ''}`;
        btn.textContent = action.label;
        if (action.disabled) btn.disabled = true;
        btn.addEventListener('click', action.onClick);
        footer.appendChild(btn);
      }
      this.dialog.appendChild(footer);
    }

    this.backdrop.appendChild(this.dialog);

    // Keyboard handler
    this.backdrop.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
      }
    });
  }

  /** Get the body element to append custom content. */
  get body(): HTMLDivElement {
    return this.bodyEl;
  }

  /** Mount the dialog into the DOM. */
  mount(parent: HTMLElement): void {
    parent.appendChild(this.backdrop);
    // Focus the first focusable element
    const focusable = this.dialog.querySelector('input, select, button') as HTMLElement;
    if (focusable) focusable.focus();
  }

  /** Close and remove the dialog. */
  close(): void {
    this.backdrop.remove();
    this.config.onClose();
    // Restore focus
    if (this.previousFocus) this.previousFocus.focus();
  }

  /** Update footer action button states. */
  setActionDisabled(index: number, disabled: boolean): void {
    const footer = this.dialog.querySelector('.dialog__footer');
    if (footer) {
      const btn = footer.children[index] as HTMLButtonElement | undefined;
      if (btn) btn.disabled = disabled;
    }
  }
}
